import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../components/PageConfigProvider/constants';
import type { TabContent } from '../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../components/PageConfigProvider/types';
import { FilterId } from '../filters/types/filters';

const roleTransitionsInflow: TabContent['items'] = [
  {
    position: { x: 0, width: 32, y: 0, height: 36 },
    id: 'role-transitions-inflow',
    title: 'Transition',
    info: {
      text: 'The most common sources of incoming workers and the most common destinations of outgoing workers.',
    },
    plot: {
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'sankey',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 6 },
    id: 'industry-transitions-inflow-role',
    title: 'Industry',
    info: {
      text: 'The composition of workers by industry.',
    },
    plot: {
      secondaryDimension: 'rics',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 6, height: 6 },
    id: 'role-transitions-inflow-geography',
    title: 'Geography',
    info: {
      text: 'The composition of workers by geography.',
    },
    plot: {
      secondaryDimension: 'geography',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 12, height: 6 },
    id: 'role-transitions-inflow-seniority',
    title: 'Seniority',
    info: {
      text: 'The composition of workers by seniority. More information on the seniority model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#seniority',
        },
      ],
    },
    plot: {
      secondaryDimension: 'seniority',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 6 },
    id: 'role-transitions-inflow-gender',
    title: 'Gender',
    info: {
      text: 'The composition of workers by gender, predicted using first name. More information on the gender model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'gender',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 24, height: 6 },
    id: 'role-transitions-inflow-ethnicity',
    title: 'Ethnicity',
    info: {
      text: 'The composition of workers by ethnicity, predicted using first name, last name, and geography. More information on the ethnicity model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'ethnicity',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 30, height: 6 },
    id: 'role-transitions-inflow-education',
    title: 'Education',
    info: {
      text: 'The composition of workers by the highest educational degree.',
    },
    plot: {
      secondaryDimension: 'education',
      defaultMetricOptions: ['transitions_inflow'],
      chartType: 'stacked-bar',
    },
  },
];
const roleTransitionsOutflow: TabContent['items'] = [
  {
    position: { x: 0, width: 32, y: 0, height: 36 },
    id: 'role-transitions-outflow',
    title: 'Transition',
    info: {
      text: 'The most common sources of incoming workers and the most common destinations of outgoing workers.',
    },
    plot: {
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'sankey',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 6 },
    id: 'role-transitions-outflow-industry',
    title: 'Industry',
    info: {
      text: 'The composition of workers by industry.',
    },
    plot: {
      secondaryDimension: 'rics',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 6, height: 6 },
    id: 'role-transitions-outflow-geography',
    title: 'Geography',
    info: {
      text: 'The composition of workers by geography.',
    },
    plot: {
      secondaryDimension: 'geography',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 12, height: 6 },
    id: 'role-transitions-outflow-seniority',
    title: 'Seniority',
    info: {
      text: 'The composition of workers by seniority. More information on the seniority model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#seniority',
        },
      ],
    },
    plot: {
      secondaryDimension: 'seniority',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 6 },
    id: 'role-transitions-outflow-gender',
    title: 'Gender',
    info: {
      text: 'The composition of workers by gender, predicted using first name. More information on the gender model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'gender',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 24, height: 6 },
    id: 'role-transitions-outflow-ethnicity',
    title: 'Ethnicity',
    info: {
      text: 'The composition of workers by ethnicity, predicted using first name, last name, and geography. More information on the ethnicity model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'ethnicity',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
  {
    position: { x: 32, width: 16, y: 30, height: 6 },
    id: 'role-transitions-outflow-education',
    title: 'Education',
    info: {
      text: 'The composition of workers by the highest educational degree.',
    },
    plot: {
      secondaryDimension: 'education',
      defaultMetricOptions: ['transitions_outflow'],
      chartType: 'stacked-bar',
    },
  },
];

const sharedConfig: Pick<
  TabContent,
  'primaryFilter' | 'secondaryFilters' | 'grid'
> = {
  primaryFilter: {
    id: FilterId.ROLE,
    selectionLimit: 1,
    selectionDefault: 1,
  },
  secondaryFilters: [
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.GEOGRAPHY,
    FilterId.SENIORITY,
    FilterId.SKILL,
    FilterId.CUSTOM_SKILL,
    FilterId.COMPANY,
    FilterId.DATE_RANGE,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
};

const roleTransitionsInflowTab: TabContent = {
  id: 'role-transitions-inflow',
  items: roleTransitionsInflow,
  ...sharedConfig,
};

const roleTransitionsOutflowTab: TabContent = {
  id: 'role-transitions-outflow',
  items: roleTransitionsOutflow,
  ...sharedConfig,
};

export const roleTransitions: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.ROLE_TRANSITIONS,
  revelioPageId: RevelioPageId.ROLE_TRANSITIONS,
  name: 'Transitions',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'role-transitions-inflow',
      label: 'Inflow',
      tabContent: roleTransitionsInflowTab,
      isActive: true,
    },
    {
      id: 'role-transitions-outflow',
      label: 'Outflow',
      tabContent: roleTransitionsOutflowTab,
      isActive: false,
    },
  ],
};
